import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Cell } from 'api';
import { useXlsxSpreadsheetNotesQuery } from 'components/Drawer/document-preview-drawer-tab/xlsx-viewer/useXlsxSpreadsheetNotesQuery';
import { XLSX_VIEWER_STRINGS } from 'components/Drawer/document-preview-drawer-tab/xlsx-viewer/xlsx-viewer.contants';
import SpreadsheetWorkbookPreview from 'components/FileViewer/spreadsheet-viewer/SpreadsheetWorkbookPreview';
import {
  getSpreadsheetNoteDisplayOptionsFromPreviewFeatures,
  INTERACTIVE_XLSX_WORKBOOK_PREVIEW_FEATURE_OPTIONS,
  resolveSpreadsheetWorkbookPreviewFeatureOptions,
} from 'components/FileViewer/spreadsheet-viewer/SpreadsheetWorkbookPreview.options';
import {
  useXlsxPresentationOverrides,
  XLSX_PREVIEW_CONTENT_STALE_TIME_IN_MS,
} from 'components/FileViewer/spreadsheet-viewer/useXlsxPresentationOverrides';
import type { MappingReference } from 'components/ScalarSpreadsheet/hooks';
import useSpreadsheetNoteTabs from 'components/SpreadsheetNotes/SpreadsheetNoteConfig/UseSpreadsheetNoteTabs/useSpreadsheetNoteTabs';
import { referenceColumnLegend, type SpreadsheetNoteData } from 'components/SpreadsheetNotes/types';
import { applyXlsxPresentationOverridesToNotes } from 'components/SpreadsheetNotes/utilities/xlsxPresentationOverrides.utils';
import { useDocuments } from 'services/hooks';
import { useCrossReferencePickStore } from 'store/useCrossReferencePickStore';
import MappingActionsToolbar from './MappingActionsToolbar';
import { clearPendingNavigation, getPendingNavigation, setPendingNavigation } from './pendingNavigation';
import { scheduleScrollMappedXlsxCellIntoView } from './xlsxViewerScrollToMappedCell';
import { XLSXViewerTabProps } from './XLSXViewerTab.props';
import type { XlsxFrozenPaneBoundariesBySheet, XlsxFrozenPaneBoundary } from './XLSXViewerTab.types';
import { EventType, useEventSubscription } from '../../../../context';
import { NavigateToDocumentReferencePayload } from '../../../../context/EventBusContext/EventBusContext.type';
import { AttributeMappingType } from '../../../../pages/Financials/financials-widget-drawer/types';
import { FinancialsCellWithReferences } from '../../../../pages/Financials/types/cell-with-references';
import { useCellMappingStore } from '../../../../store';
import { Logger } from '../../../../utilities';
import adjustAlphaFromColorString from '../../../../utilities/adjust-alpha-in-color-string/adjust-alpha-from-color-string';
import { convertedColors } from '../../../ScalarSpreadsheet/constants';
import { useAttributeMappedWidgetDrawer } from '../../attribute-mapped-widget-drawer/AttributeMappedWidgetDrawerContext';
import { WidgetDrawerApplicationContext } from '../../widget-drawer/WidgetDrawer.props';

const DRAWER_XLSX_PREVIEW_FEATURE_OPTIONS = {
  ...INTERACTIVE_XLSX_WORKBOOK_PREVIEW_FEATURE_OPTIONS,
  search: true,
  stickyReferenceRow: true,
};
const REFERENCE_COLUMN_CELL_KEY = `${referenceColumnLegend}0`;
const REFERENCE_ROW_CELL_KEY = REFERENCE_COLUMN_CELL_KEY;

const getColumnLegendFromCellKey = (cellKey?: string) => cellKey?.replace(/\d+$/, '');
const getRowIndexFromCellKey = (cellKey?: string) => cellKey?.match(/\d+$/)?.[0];
const shouldStoreFrozenPaneBoundary = (boundary: XlsxFrozenPaneBoundary) =>
  Boolean(
    boundary.columnCellKey ||
    boundary.rowCellKey ||
    boundary.isReferenceColumnFrozen === false ||
    boundary.isReferenceRowFrozen === false
  );

const getSheetStateKey = (
  note: SpreadsheetNoteData | undefined,
  documentReferenceId: number,
  sheetIndex: number
): string => {
  if (note?.id != null) {
    return String(note.id);
  }
  if (note?.temp_id) {
    return String(note.temp_id);
  }
  return `xlsx-preview-${documentReferenceId}-${sheetIndex}`;
};

const XLSXViewerTab = ({ document }: XLSXViewerTabProps) => {
  const {
    attributeMapper,
    attributeReferenceConfig,
    handlePushingAttributeMappings,
    handleClearMappings,
    onReprocessDocument,
    mappingReprocessPrimaryLabel,
    applicationContext,
    showPushMappings,
  } = useAttributeMappedWidgetDrawer();
  const { updateCellMapping } = useCellMappingStore();
  const { downloadDocumentContent } = useDocuments();
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [frozenPaneBoundariesBySheet, setFrozenPaneBoundariesBySheet] = useState<XlsxFrozenPaneBoundariesBySheet>({});
  const previewFeatureOptions = useMemo(
    () => resolveSpreadsheetWorkbookPreviewFeatureOptions(DRAWER_XLSX_PREVIEW_FEATURE_OPTIONS),
    []
  );
  const handleReprocess = useCallback(() => {
    onReprocessDocument?.(document);
  }, [onReprocessDocument, document]);

  // Freeze pane choices are workbook-local; switching documents should restore the default viewer layout.
  useEffect(() => {
    setFrozenPaneBoundariesBySheet({});
  }, [document.document_reference_id]);

  const { data, error, isLoading, isPolling } = useXlsxSpreadsheetNotesQuery(document.file.id);
  const { data: documentContent, isLoading: isDocumentContentLoading } = useQuery({
    enabled: Boolean(document.document_reference_id),
    queryFn: () =>
      downloadDocumentContent(document, {
        fallbackToSignedUrl: true,
        showErrorNotification: false,
      }),
    queryKey: ['xlsx-viewer-source-content', document.document_reference_id, document.file.id],
    retry: false,
    staleTime: XLSX_PREVIEW_CONTENT_STALE_TIME_IN_MS,
  });
  const { presentationOverrides, isLoading: isPresentationOverridesLoading } = useXlsxPresentationOverrides({
    base64Content: documentContent?.content ?? undefined,
    fileId: document.file.id ?? document.document_reference_id,
    isSourceLoading: isDocumentContentLoading,
    sourceUrl: documentContent?.blob_url ?? undefined,
  });

  const notes = useMemo(() => {
    if (!data || data.length === 0) {
      return [];
    }
    return data;
  }, [data]);
  const notesWithPresentationOverrides = useMemo(
    () => applyXlsxPresentationOverridesToNotes(notes, presentationOverrides),
    [notes, presentationOverrides]
  );
  const activeSheetStateKey = getSheetStateKey(
    notesWithPresentationOverrides[activeTabIndex],
    document.document_reference_id,
    activeTabIndex
  );
  const frozenPaneBoundary = frozenPaneBoundariesBySheet[activeSheetStateKey];
  const isReferenceColumnFrozen =
    !frozenPaneBoundary?.columnCellKey && frozenPaneBoundary?.isReferenceColumnFrozen !== false;
  const effectiveFrozenPaneColumnCellKey =
    frozenPaneBoundary?.columnCellKey ?? (isReferenceColumnFrozen ? REFERENCE_COLUMN_CELL_KEY : undefined);
  const isReferenceRowFrozen =
    !frozenPaneBoundary?.rowCellKey && frozenPaneBoundary?.isReferenceRowFrozen !== false;
  const effectiveFrozenPaneRowCellKey =
    frozenPaneBoundary?.rowCellKey ?? (isReferenceRowFrozen ? REFERENCE_ROW_CELL_KEY : undefined);
  const activePreviewFeatureOptions = useMemo(
    () => ({
      ...previewFeatureOptions,
      // Custom panes reuse the sticky-reference layout while replacing the default alpha column or Years row.
      stickyReferenceColumn: isReferenceColumnFrozen || Boolean(frozenPaneBoundary?.columnCellKey),
      stickyReferenceRow: isReferenceRowFrozen || Boolean(frozenPaneBoundary?.rowCellKey),
    }),
    [
      frozenPaneBoundary?.columnCellKey,
      frozenPaneBoundary?.rowCellKey,
      isReferenceColumnFrozen,
      isReferenceRowFrozen,
      previewFeatureOptions,
    ]
  );

  const handleToggleFrozenColumnsAtCell = useCallback(
    (cellKey: string) => {
      setFrozenPaneBoundariesBySheet(currentBoundaries => {
        const currentBoundary = currentBoundaries[activeSheetStateKey] ?? {};
        const selectedColumnLegend = getColumnLegendFromCellKey(cellKey);
        const currentColumnLegend = getColumnLegendFromCellKey(currentBoundary.columnCellKey);
        const isReferenceColumn = selectedColumnLegend === referenceColumnLegend;
        const isCurrentlyFrozenReferenceColumn =
          currentBoundary.isReferenceColumnFrozen !== false && !currentBoundary.columnCellKey;
        const nextBoundary = isReferenceColumn
          ? {
            ...currentBoundary,
            columnCellKey: undefined,
            isReferenceColumnFrozen: isCurrentlyFrozenReferenceColumn ? false : undefined,
          }
          : {
            ...currentBoundary,
            columnCellKey: currentColumnLegend === selectedColumnLegend ? undefined : cellKey,
          };
        const nextBoundaries = { ...currentBoundaries };
        if (shouldStoreFrozenPaneBoundary(nextBoundary)) {
          nextBoundaries[activeSheetStateKey] = nextBoundary;
        } else {
          delete nextBoundaries[activeSheetStateKey];
        }
        return nextBoundaries;
      });
    },
    [activeSheetStateKey]
  );
  const handleToggleFrozenRowsAtCell = useCallback(
    (cellKey: string) => {
      setFrozenPaneBoundariesBySheet(currentBoundaries => {
        const currentBoundary = currentBoundaries[activeSheetStateKey] ?? {};
        const selectedRowIndex = getRowIndexFromCellKey(cellKey);
        const currentRowIndex = getRowIndexFromCellKey(currentBoundary.rowCellKey);
        const isReferenceRow = selectedRowIndex === '0';
        const isCurrentlyFrozenReferenceRow =
          currentBoundary.isReferenceRowFrozen !== false && !currentBoundary.rowCellKey;
        const nextBoundary = isReferenceRow
          ? {
            ...currentBoundary,
            isReferenceRowFrozen: isCurrentlyFrozenReferenceRow ? false : undefined,
            rowCellKey: undefined,
          }
          : {
            ...currentBoundary,
            rowCellKey: currentRowIndex === selectedRowIndex ? undefined : cellKey,
          };
        const nextBoundaries = { ...currentBoundaries };
        if (shouldStoreFrozenPaneBoundary(nextBoundary)) {
          nextBoundaries[activeSheetStateKey] = nextBoundary;
        } else {
          delete nextBoundaries[activeSheetStateKey];
        }
        return nextBoundaries;
      });
    },
    [activeSheetStateKey]
  );
  const drawerXlsxPreviewDisplayOptions = useMemo(
    () => ({
      ...getSpreadsheetNoteDisplayOptionsFromPreviewFeatures(previewFeatureOptions),
      allowRowLegendSelection: true,
      frozenPaneColumnCellKey: effectiveFrozenPaneColumnCellKey,
      frozenPaneRowCellKey: effectiveFrozenPaneRowCellKey,
      onToggleFrozenColumnsAtCell: handleToggleFrozenColumnsAtCell,
      onToggleFrozenRowsAtCell: handleToggleFrozenRowsAtCell,
    }),
    [
      effectiveFrozenPaneColumnCellKey,
      effectiveFrozenPaneRowCellKey,
      handleToggleFrozenColumnsAtCell,
      handleToggleFrozenRowsAtCell,
      previewFeatureOptions,
    ]
  );

  const isAttributeMapperEnabled = !!attributeMapper;

  // Show mapping actions when a mapper exists or when reprocess/create-labels is available.
  // Financials initially has no mapper until a FINANCIAL_MAPPING job runs; hiding the toolbar blocked "Create Labels".
  const showMappingActionsToolbar =
    Boolean(handleClearMappings) && (isAttributeMapperEnabled || Boolean(onReprocessDocument));

  const { tabs, workbookContextValue, cells, isWorkbookCurrent } = useSpreadsheetNoteTabs({
    notes: notesWithPresentationOverrides,
    actionsAreDisabled: false,
    isAttributeMapperEnabled,
    attributeReferenceConfig,
    attributeMapper,
    documentId: document.document_reference_id,
    displayOptions: drawerXlsxPreviewDisplayOptions,
  });

  const activeTab = tabs[activeTabIndex] ?? null;

  // The overrides stage keeps the grid behind a skeleton and its arrival rebuilds every
  // cell, wiping earlier highlights; isWorkbookCurrent stays false until the committed
  // cells were produced by the current notes build. react-query v5 reports isLoading
  // false for disabled and errored queries, so a document without downloadable content
  // (or a failed download) settles immediately and snaps against the un-styled grid
  // instead of blocking forever.
  const isNavigationReady = !isLoading && !isPolling && !isPresentationOverridesLoading && isWorkbookCurrent;

  const activateSheetTab = useCallback((nextIndex: number) => {
    setActiveTabIndex(prevIndex => (prevIndex === nextIndex ? prevIndex : nextIndex));
  }, []);

  // Keep the selected sheet valid when the converted workbook changes shape.
  useEffect(() => {
    if (!tabs.length) {
      return;
    }
    if (activeTabIndex < tabs.length && !tabs[activeTabIndex]?.disabled && !tabs[activeTabIndex]?.isActionTab) {
      return;
    }
    const firstAvailableIndex = tabs.findIndex(tab => !tab.disabled && !tab.isActionTab);
    activateSheetTab(firstAvailableIndex === -1 ? 0 : firstAvailableIndex);
  }, [activeTabIndex, activateSheetTab, tabs]);

  const normalizationRegex = useMemo(() => /[^a-z0-9]/g, []);

  const navigateToSheet = useCallback(
    (sheetName: string) => {
      if (!tabs?.length || !sheetName) {
        return;
      }
      const normalizedTarget = sheetName.toLowerCase().replace(normalizationRegex, '');
      const targetIndex = tabs.findIndex(tab => {
        const label = typeof tab.label === 'string' ? tab.label : '';
        return label.toLowerCase().replace(normalizationRegex, '') === normalizedTarget;
      });
      if (targetIndex !== -1) {
        activateSheetTab(targetIndex);
      }
    },
    [activateSheetTab, tabs, normalizationRegex]
  );

  const clearHighlights = useCallback(() => {
    if (!cells) {
      return;
    }

    Object.values(cells).forEach(sheet => {
      Object.values(sheet).forEach(cell => {
        cell.setCellBackgroundColor(undefined);
        const nextStyle = { ...(cell.style ?? {}) };
        delete nextStyle.boxShadow;
        // eslint-disable-next-line no-param-reassign
        cell.style = Object.keys(nextStyle).length ? nextStyle : undefined;
        cell.dispatchEvent();
      });
    });
  }, [cells]);

  // Case/punctuation-insensitive sheet lookup, shared by the painter and the
  // navigation guard so both agree on whether a payload's sheet exists.
  const resolveSheetCells = useCallback(
    (sheetName: string) => {
      if (!cells) {
        return undefined;
      }
      const normalize = (value: string) => value.toLowerCase().replaceAll(/[^a-z0-9]/g, '');
      const targetNormalized = normalize(sheetName);
      const originalSheetName = Object.keys(cells).find(name => normalize(name) === targetNormalized);
      return originalSheetName ? cells[originalSheetName] : undefined;
    },
    [cells]
  );

  // Paint refs on one sheet and return the first resolved cell for scrolling.
  const paintMappedCellsOnSheet = useCallback(
    (cellRefs: { ref: string }[], sheetName: string): string | null => {
      const sheetCells = resolveSheetCells(sheetName);
      if (!sheetCells) {
        return null;
      }

      let firstRef: string | null = null;
      cellRefs.forEach((cellRef, index) => {
        const rangeMatch = cellRef.ref.match(/^([A-Z]+)(\d+):([A-Z]+)(\d+)$/i);
        const expanded: string[] = [];
        // cellRef.ref could be a range of cells such as A15:A19.
        if (rangeMatch) {
          const [, startCol, startRowStr, endCol, endRowStr] = rangeMatch;
          const startColUpper = startCol.toUpperCase();
          // If the far corner is outside the sheet, keep only the reliable source anchor.
          if (!sheetCells[`${endCol.toUpperCase()}${endRowStr}`]) {
            expanded.push(`${startColUpper}${startRowStr}`);
          } else {
            const lowestRowNumber = Math.min(+startRowStr, +endRowStr);
            const highestRowNumber = Math.max(+startRowStr, +endRowStr);
            for (let rowNum = lowestRowNumber; rowNum <= highestRowNumber; rowNum++) {
              expanded.push(`${startColUpper}${rowNum}`);
            }
          }
        } else {
          expanded.push(cellRef.ref);
        }

        const color = convertedColors[index % convertedColors.length];
        const borderColor = adjustAlphaFromColorString(color, 0.6);
        expanded.forEach(ref => {
          const updatedCell = sheetCells[ref];
          if (!updatedCell) {
            return;
          }
          if (firstRef == null) {
            firstRef = ref;
          }
          updatedCell.setCellBackgroundColor(color);
          // eslint-disable-next-line no-param-reassign
          updatedCell.style = {
            ...(updatedCell.style ?? {}),
            boxShadow: `inset 0 0 0 3px ${borderColor}`,
          };
          updatedCell.dispatchEvent();
        });
      });
      return firstRef;
    },
    [resolveSheetCells]
  );

  // Return false when a cold-open navigate target is not mounted yet so the pending payload can retry.
  const highlightMappedCells = useCallback(
    (cellRefs: { ref: string }[], activeSheetName?: string): boolean => {
      // Resolve the sheet before clearing; invalid or unmatchable payloads (e.g. a
      // renamed sheet) must not wipe existing highlights, least of all on every retry.
      if (!cellRefs?.length || !activeSheetName || !resolveSheetCells(activeSheetName)) {
        return false;
      }
      clearHighlights();
      const firstRef = paintMappedCellsOnSheet(cellRefs, activeSheetName);
      if (firstRef) {
        scheduleScrollMappedXlsxCellIntoView(firstRef);
      }
      return firstRef != null;
    },
    [resolveSheetCells, clearHighlights, paintMappedCellsOnSheet]
  );

  // Apply warm or pending navigation; false means the grid cannot take it yet (still
  // loading, workbook rebuild in flight, or the target sheet missing) and the payload
  // should stay stashed so the pending-navigation effect retries on the next build.
  const consumeNavigation = useCallback(
    (nav: { sheetName?: string; cellReferences?: { ref: string }[] }): boolean => {
      if (!isNavigationReady) {
        return false;
      }
      if (nav.sheetName) {
        navigateToSheet(nav.sheetName);
      }
      if (!nav.cellReferences?.length) {
        return true;
      }
      return highlightMappedCells(nav.cellReferences, nav.sheetName);
    },
    [isNavigationReady, navigateToSheet, highlightMappedCells]
  );

  // Warm open: consume only when the settled grid can take it; otherwise re-stash the
  // payload (last navigation wins) so the pending-navigation effect replays it later.
  useEventSubscription(EventType.NAVIGATE_TO_DOCUMENT_REFERENCE, (payload: NavigateToDocumentReferencePayload) => {
    if (payload.documentReferenceId !== document.document_reference_id) {
      return;
    }
    if (consumeNavigation(payload)) {
      clearPendingNavigation();
    } else {
      setPendingNavigation(payload);
    }
  });

  // Cold open: retry stashed navigation once sheet cells become available.
  useEffect(() => {
    const pending = getPendingNavigation();
    if (!pending || pending.documentReferenceId !== document.document_reference_id) {
      return;
    }
    if (consumeNavigation(pending)) {
      clearPendingNavigation();
    }
  }, [tabs, document.document_reference_id, consumeNavigation]);

  // A payload still stashed when this document's viewer goes away means the user
  // abandoned the jump; drop it so the module-global slot cannot replay a stale
  // navigation on some later plain open of the same document.
  useEffect(
    () => () => {
      if (getPendingNavigation()?.documentReferenceId === document.document_reference_id) {
        clearPendingNavigation();
      }
    },
    [document.document_reference_id]
  );

  useEventSubscription(
    EventType.SPREADSHEET_ACTIVE_CELL_CHANGED,
    ([newCell]: [{ cellInfo: FinancialsCellWithReferences }, FinancialsCellWithReferences]) => {
      // Cell-pick mode wins over attribute mapping because it is the user's latest explicit action.
      const activePickTarget = useCrossReferencePickStore.getState().activeTarget;
      if (activePickTarget) {
        const mappingReferences: MappingReference[] = [
          {
            document_reference: String(document.document_reference_id),
            sheet_name: typeof activeTab?.label === 'string' ? activeTab.label : '',
            cell_references: [{ ref: String(newCell.cellInfo.key ?? '') }],
          },
        ];
        clearHighlights();
        const rawValue = newCell.cellInfo.value;
        const value: number | string | null =
          typeof rawValue === 'number' || typeof rawValue === 'string' ? rawValue : null;
        useCrossReferencePickStore.getState().resolve({
          value,
          mappingReferences,
        });
        return;
      }

      if (attributeReferenceConfig?.selectionMode === AttributeMappingType.SINGLE_CELL && attributeMapper) {
        Logger.debug('Handling single cell avoiding highlights');
        const cellInfo = newCell.cellInfo as { key?: string; data?: { sheetName?: string } };
        const sheetFromCell = cellInfo?.data?.sheetName;
        const sheetFromTab = typeof activeTab?.label === 'string' ? activeTab.label : '';
        const resolvedSheet =
          typeof sheetFromCell === 'string' && sheetFromCell.length > 0 ? sheetFromCell : sheetFromTab;
        handlePushingAttributeMappings?.({
          documentReferencePayload: {
            documentReferenceId: document.document_reference_id,
            sheet: resolvedSheet,
          },
          selectedCell: newCell,
        });
      }

      clearHighlights();

      if (!cells || !newCell.cellInfo.mappingReferences?.length) {
        return;
      }

      // Reuse the shared painter so range expansion and colors stay consistent.
      let cellKeyToScroll: string | null = null;
      newCell.cellInfo.mappingReferences.forEach(item => {
        const firstRef = paintMappedCellsOnSheet(item.cell_references, item.sheet_name);
        if (firstRef && cellKeyToScroll == null && item.document_reference === document.document_reference_id) {
          cellKeyToScroll = firstRef;
        }
      });

      if (cellKeyToScroll) {
        scheduleScrollMappedXlsxCellIntoView(cellKeyToScroll);
      }
    }
  );

  useEffect(() => {
    if (isLoading) {
      return;
    }

    // Include document ID in the key so same-named sheets across documents do not collide.
    const documentId = document.document_reference_id;
    const cellMapping = notes
      .filter(note => note.content)
      .reduce(
        (acc, note) => {
          const { cells: mappedCells } = note.content;
          const name = note.name ?? '';
          const compositeKey = `${documentId}_${name}`;
          acc[compositeKey] = mappedCells ?? {};
          acc[name] = mappedCells ?? {};
          return acc;
        },
        {} as Record<string, Record<string, Cell>>
      );

    updateCellMapping(cellMapping);
  }, [notes, updateCellMapping, isLoading, document.document_reference_id]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.ctrlKey && e.key === 'p') {
        e.preventDefault();
        handlePushingAttributeMappings?.({
          documentReferencePayload: {
            documentReferenceId: document.document_reference_id,
            sheet: `${activeTab?.label}`,
          },
        });
      }
    },
    [handlePushingAttributeMappings, activeTab?.label, document.document_reference_id]
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  // "Create Labels" is a Financials-only affordance; its behavior on the cap table is
  // undefined, so the primary reprocess action is gated strictly to the Financials context.
  const showPrimaryMappingReprocess = useMemo(
    () => Boolean(onReprocessDocument) && applicationContext === WidgetDrawerApplicationContext.FINANCIALS,
    [onReprocessDocument, applicationContext]
  );
  const viewerAriaLabel = `${document.file?.filename ?? 'Spreadsheet'} preview`;

  return (
    <SpreadsheetWorkbookPreview
      actions={leadingActions =>
        showMappingActionsToolbar && handleClearMappings ? (
          <MappingActionsToolbar
            documentReferenceId={document.document_reference_id}
            activeSheetLabel={activeTab?.label as string | undefined}
            leadingActions={leadingActions}
            onPushMappings={() => handlePushingAttributeMappings?.()}
            onClearMappings={handleClearMappings}
            onReprocessDocument={handleReprocess}
            primaryReprocessLabel={mappingReprocessPrimaryLabel}
            showCreateLabels={showPrimaryMappingReprocess}
            showPushMappings={Boolean(showPushMappings && isAttributeMapperEnabled)}
          />
        ) : (
          leadingActions
        )
      }
      activeTabIndex={activeTabIndex}
      ariaLabel={viewerAriaLabel}
      compensateAppZoom
      error={error}
      errorMessage={XLSX_VIEWER_STRINGS.ERROR.MESSAGE}
      errorTitle={XLSX_VIEWER_STRINGS.ERROR.TITLE}
      featureOptions={activePreviewFeatureOptions}
      freezePaneColumnCellKey={frozenPaneBoundary?.columnCellKey}
      freezePaneRowCellKey={frozenPaneBoundary?.rowCellKey}
      isLoading={((isLoading || isPresentationOverridesLoading) && !error && !isPolling) || isPolling}
      loadingMessage={isPolling || isPresentationOverridesLoading ? XLSX_VIEWER_STRINGS.LOADING.MESSAGE : undefined}
      onActiveTabIndexChange={activateSheetTab}
      resetKey={document.document_reference_id}
      searchShortcutScope="mounted"
      tabValuePrefix="xlsx-preview-sheet"
      tabs={tabs}
      testId="xlsx-viewer-workbook"
      workbookContextValue={workbookContextValue}
    />
  );
};
export default XLSXViewerTab;
